import { type ReactNode, useEffect, useRef } from "react";
import { useNavigate } from "react-router";

import { authModule } from "@/src/auth/auth";
import { useAuth } from "@/src/auth/hooks/useAuth";

interface AuthInitializationProviderProps {
  children: ReactNode;
}

const AuthInitializationProvider = ({
  children
}: AuthInitializationProviderProps) => {
  const initialLoginAttemptedRef = useRef(false);
  const { attemptLoginOnInitialLoad, completeRedirectLogin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (initialLoginAttemptedRef.current) return;
    const attemptLogin = async () => {
      const searchParams = new URLSearchParams(window.location.search);
      const code = searchParams.get("code");
      const state = searchParams.get("state");

      // Handle Auth0 redirect callback (both consumer/admin redirect login and dealer SSO)
      if (code && state) {
        initialLoginAttemptedRef.current = true;
        try {
          // alma-account-core's in-memory singleton is wiped on redirect — must
          // re-initialize before handleAuthentication() can exchange the auth code.
          const storedConnection = localStorage.getItem("AUTH_CONNECTION");
          const previousSession = localStorage.getItem("PREVIOUS_SESSION") as
            | "consumer"
            | "dealer"
            | "admin"
            | null;
          const userGroup = storedConnection
            ? "dealer"
            : (previousSession ?? "consumer");
          await authModule.initAuth(userGroup, storedConnection ?? undefined);

          const result = await authModule.handleAuthentication();

          // Dealer SSO — has AUTH_CONNECTION stored before redirect
          if (storedConnection) {
            window.location.replace("/dealer/all-active");
            return;
          }

          // Consumer/admin redirect login.
          // Fetch user state here so the user arrives at returnUrl already logged in
          // (no second page reload needed).
          const appState = result?.appState;
          const rawUserGroup = appState?.userGroup as string | undefined;
          const validUserGroups = ["consumer", "dealer", "admin"] as const;
          const returnUserGroup = (
            validUserGroups.includes(
              rawUserGroup as (typeof validUserGroups)[number]
            )
              ? rawUserGroup
              : userGroup
          ) as "consumer" | "dealer" | "admin";
          try {
            await completeRedirectLogin(returnUserGroup);
          } catch {
            // Error notification already shown by completeRedirectLogin
            navigate("/", { replace: true });
            return;
          }

          const returnUrl = appState?.returnUrl as string | undefined;
          if (returnUrl) {
            try {
              const parsed = new URL(returnUrl, window.location.origin);
              if (parsed.origin === window.location.origin) {
                navigate(parsed.pathname + parsed.search + parsed.hash, {
                  replace: true
                });
                return;
              }
            } catch {
              // invalid URL — fall through to default
            }
          }

          navigate("/", { replace: true });
        } catch (error) {
          console.error("handleAuthentication failed:", error);
          window.location.replace("/");
        }
        return;
      }

      // Normal initialization flow
      if (!initialLoginAttemptedRef.current) {
        initialLoginAttemptedRef.current = true;
        const routeType = globalThis.location.pathname.startsWith("/dealer")
          ? "dealer"
          : globalThis.location.pathname.startsWith("/admin")
            ? "admin"
            : "consumer";
        const connection =
          localStorage.getItem("AUTH_CONNECTION") ||
          searchParams.get("connection");
        await attemptLoginOnInitialLoad(routeType, connection);
      }
    };

    attemptLogin();
  }, [attemptLoginOnInitialLoad, completeRedirectLogin, navigate]);

  return children;
};

export { AuthInitializationProvider };
